
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2, Zap, Brain, Eye, EyeOff, Save, TestTube } from "lucide-react";

interface ApiSettings {
  id: string;
  openai_enabled: boolean;
  deepseek_enabled: boolean;
  default_api: string;
  openai_key_configured: boolean;
  deepseek_key_configured: boolean;
  google_vision_key_configured: boolean;
}

interface ApiKeyState {
  value: string;
  showPassword: boolean;
  isConfigured: boolean;
}

const ApiControls = () => {
  const [settings, setSettings] = useState<ApiSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState<string | null>(null);
  
  const [apiKeys, setApiKeys] = useState<{
    openai: ApiKeyState;
    deepseek: ApiKeyState;
    googleVision: ApiKeyState;
  }>({
    openai: { value: '', showPassword: false, isConfigured: false },
    deepseek: { value: '', showPassword: false, isConfigured: false },
    googleVision: { value: '', showPassword: false, isConfigured: false },
  });

  useEffect(() => {
    fetchApiSettings();
  }, []);

  const fetchApiSettings = async () => {
    try {
      const { data, error } = await supabase.rpc('get_api_keys_secure');
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Nenhuma configuração encontrada');
      }
      
      const settings = data[0];
      setSettings({
        id: 'default', // We don't need the actual ID for security
        openai_enabled: settings.openai_enabled,
        deepseek_enabled: settings.deepseek_enabled,
        default_api: settings.default_api,
        openai_key_configured: settings.openai_key_configured,
        deepseek_key_configured: settings.deepseek_key_configured,
        google_vision_key_configured: settings.google_vision_key_configured,
      });
      
      // Update API key states based on what's configured
      setApiKeys(prev => ({
        openai: { 
          ...prev.openai, 
          isConfigured: settings.openai_key_configured,
          value: settings.openai_key_configured ? '••••••••••••••••' : ''
        },
        deepseek: { 
          ...prev.deepseek, 
          isConfigured: settings.deepseek_key_configured,
          value: settings.deepseek_key_configured ? '••••••••••••••••' : ''
        },
        googleVision: { 
          ...prev.googleVision, 
          isConfigured: settings.google_vision_key_configured,
          value: settings.google_vision_key_configured ? '••••••••••••••••' : ''
        },
      }));
    } catch (error) {
      console.error('Error fetching API settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações da API.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateApiSettings = async (updatedSettings: Partial<ApiSettings>) => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('api_settings')
        .update(updatedSettings)
        .eq('id', settings.id);
      
      if (error) throw error;
      
      setSettings({ ...settings, ...updatedSettings });
      toast({
        title: "Configurações atualizadas",
        description: "As configurações da API foram atualizadas com sucesso.",
      });
    } catch (error) {
      console.error('Error updating API settings:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar as configurações da API.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleOpenAI = (enabled: boolean) => {
    updateApiSettings({ openai_enabled: enabled });
  };

  const handleToggleDeepSeek = (enabled: boolean) => {
    updateApiSettings({ deepseek_enabled: enabled });
  };

  const handleDefaultApiChange = (defaultApi: string) => {
    updateApiSettings({ default_api: defaultApi });
  };

  const handleApiKeyChange = (apiType: 'openai' | 'deepseek' | 'googleVision', value: string) => {
    setApiKeys(prev => ({
      ...prev,
      [apiType]: { ...prev[apiType], value }
    }));
  };

  const togglePasswordVisibility = (apiType: 'openai' | 'deepseek' | 'googleVision') => {
    setApiKeys(prev => ({
      ...prev,
      [apiType]: { ...prev[apiType], showPassword: !prev[apiType].showPassword }
    }));
  };

  const saveApiKey = async (apiType: 'openai' | 'deepseek' | 'googleVision') => {
    if (!settings) return;

    const keyValue = apiKeys[apiType].value;
    if (!keyValue || keyValue === '••••••••••••••••') return;

    setIsSaving(true);
    try {
      const { error } = await supabase.functions.invoke('update-api-keys', {
        body: {
          [`${apiType === 'googleVision' ? 'google_vision' : apiType}_key`]: keyValue
        }
      });

      if (error) throw error;

      toast({
        title: "Chave salva",
        description: `Chave da API ${apiType} foi salva com sucesso.`,
      });

      // Update local state
      setApiKeys(prev => ({
        ...prev,
        [apiType]: { 
          ...prev[apiType], 
          isConfigured: true,
          value: '••••••••••••••••',
          showPassword: false
        }
      }));

    } catch (error) {
      console.error('Error saving API key:', error);
      toast({
        title: "Erro",
        description: `Não foi possível salvar a chave da API ${apiType}.`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const testApiConnection = async (apiType: 'openai' | 'deepseek' | 'googleVision') => {
    setIsTesting(apiType);
    try {
      // Simulate API test - in a real implementation, you'd call a test endpoint
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Conexão testada",
        description: `API ${apiType} está funcionando corretamente.`,
      });
    } catch (error) {
      toast({
        title: "Erro na conexão",
        description: `Falha ao testar a API ${apiType}.`,
        variant: "destructive",
      });
    } finally {
      setIsTesting(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Configurações de API
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Configurações de API
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Erro ao carregar configurações.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Configurações de API
        </CardTitle>
        <CardDescription>
          Controle quais APIs de IA estão ativas no sistema. As chaves de API são criptografadas e armazenadas de forma segura no banco de dados.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* OpenAI Toggle */}
        <div className="flex items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <Zap className="h-4 w-4 text-green-600" />
            <Label htmlFor="openai-toggle" className="text-sm font-medium">
              OpenAI API
            </Label>
          </div>
          <Switch
            id="openai-toggle"
            checked={settings.openai_enabled}
            onCheckedChange={handleToggleOpenAI}
            disabled={isSaving}
          />
        </div>

        {/* DeepSeek Toggle */}
        <div className="flex items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <Brain className="h-4 w-4 text-blue-600" />
            <Label htmlFor="deepseek-toggle" className="text-sm font-medium">
              DeepSeek API
            </Label>
          </div>
          <Switch
            id="deepseek-toggle"
            checked={settings.deepseek_enabled}
            onCheckedChange={handleToggleDeepSeek}
            disabled={isSaving}
          />
        </div>

        {/* API Keys Management */}
        <div className="space-y-6 pt-4 border-t">
          <h3 className="text-lg font-semibold">Gerenciamento de Chaves API</h3>
          
          {/* OpenAI API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4 text-green-600" />
                Chave OpenAI API
              </Label>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${apiKeys.openai.isConfigured ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-xs text-muted-foreground">
                  {apiKeys.openai.isConfigured ? 'Configurada' : 'Não configurada'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={apiKeys.openai.showPassword ? "text" : "password"}
                  value={apiKeys.openai.value}
                  onChange={(e) => handleApiKeyChange('openai', e.target.value)}
                  placeholder="sk-..."
                  disabled={isSaving}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => togglePasswordVisibility('openai')}
                >
                  {apiKeys.openai.showPassword ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </Button>
              </div>
              <Button
                onClick={() => saveApiKey('openai')}
                disabled={isSaving || !apiKeys.openai.value || apiKeys.openai.value === '••••••••••••••••'}
                size="sm"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
              <Button
                onClick={() => testApiConnection('openai')}
                disabled={!apiKeys.openai.isConfigured || isTesting === 'openai'}
                variant="outline"
                size="sm"
              >
                {isTesting === 'openai' ? 
                  <Loader2 className="h-4 w-4 animate-spin" /> : 
                  <TestTube className="h-4 w-4" />
                }
                Testar
              </Button>
            </div>
          </div>

          {/* DeepSeek API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Brain className="h-4 w-4 text-blue-600" />
                Chave DeepSeek API
              </Label>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${apiKeys.deepseek.isConfigured ? 'bg-blue-500' : 'bg-gray-300'}`} />
                <span className="text-xs text-muted-foreground">
                  {apiKeys.deepseek.isConfigured ? 'Configurada' : 'Não configurada'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={apiKeys.deepseek.showPassword ? "text" : "password"}
                  value={apiKeys.deepseek.value}
                  onChange={(e) => handleApiKeyChange('deepseek', e.target.value)}
                  placeholder="sk-..."
                  disabled={isSaving}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => togglePasswordVisibility('deepseek')}
                >
                  {apiKeys.deepseek.showPassword ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </Button>
              </div>
              <Button
                onClick={() => saveApiKey('deepseek')}
                disabled={isSaving || !apiKeys.deepseek.value || apiKeys.deepseek.value === '••••••••••••••••'}
                size="sm"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
              <Button
                onClick={() => testApiConnection('deepseek')}
                disabled={!apiKeys.deepseek.isConfigured || isTesting === 'deepseek'}
                variant="outline"
                size="sm"
              >
                {isTesting === 'deepseek' ? 
                  <Loader2 className="h-4 w-4 animate-spin" /> : 
                  <TestTube className="h-4 w-4" />
                }
                Testar
              </Button>
            </div>
          </div>

          {/* Google Vision API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Eye className="h-4 w-4 text-purple-600" />
                Chave Google Vision API
              </Label>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${apiKeys.googleVision.isConfigured ? 'bg-purple-500' : 'bg-gray-300'}`} />
                <span className="text-xs text-muted-foreground">
                  {apiKeys.googleVision.isConfigured ? 'Configurada' : 'Não configurada'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={apiKeys.googleVision.showPassword ? "text" : "password"}
                  value={apiKeys.googleVision.value}
                  onChange={(e) => handleApiKeyChange('googleVision', e.target.value)}
                  placeholder="AIza..."
                  disabled={isSaving}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => togglePasswordVisibility('googleVision')}
                >
                  {apiKeys.googleVision.showPassword ? 
                    <EyeOff className="h-4 w-4" /> : 
                    <Eye className="h-4 w-4" />
                  }
                </Button>
              </div>
              <Button
                onClick={() => saveApiKey('googleVision')}
                disabled={isSaving || !apiKeys.googleVision.value || apiKeys.googleVision.value === '••••••••••••••••'}
                size="sm"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar
              </Button>
              <Button
                onClick={() => testApiConnection('googleVision')}
                disabled={!apiKeys.googleVision.isConfigured || isTesting === 'googleVision'}
                variant="outline"
                size="sm"
              >
                {isTesting === 'googleVision' ? 
                  <Loader2 className="h-4 w-4 animate-spin" /> : 
                  <TestTube className="h-4 w-4" />
                }
                Testar
              </Button>
            </div>
          </div>
        </div>

        {/* Default API Selector */}
        <div className="space-y-2 pt-4 border-t">
          <Label className="text-sm font-medium">API Padrão</Label>
          <Select
            value={settings.default_api}
            onValueChange={handleDefaultApiChange}
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a API padrão" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai" disabled={!settings.openai_enabled}>
                OpenAI {!settings.openai_enabled && "(Desativada)"}
              </SelectItem>
              <SelectItem value="deepseek" disabled={!settings.deepseek_enabled}>
                DeepSeek {!settings.deepseek_enabled && "(Desativada)"}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Status */}
        <div className="pt-4 border-t">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${settings.openai_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
              <span className={settings.openai_enabled ? 'text-green-700' : 'text-gray-500'}>
                OpenAI {settings.openai_enabled ? 'Ativa' : 'Inativa'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${settings.deepseek_enabled ? 'bg-blue-500' : 'bg-gray-300'}`} />
              <span className={settings.deepseek_enabled ? 'text-blue-700' : 'text-gray-500'}>
                DeepSeek {settings.deepseek_enabled ? 'Ativa' : 'Inativa'}
              </span>
            </div>
          </div>
        </div>

        {isSaving && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Salvando...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApiControls;
